import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Phone, PhoneCall, TrendingUp, Clock, ShieldAlert } from "lucide-react";

const ROLE_TONE = {
  admin:              "bg-red-50 text-red-700 border-red-200",
  acquisition_manager:"bg-emerald-50 text-emerald-700 border-emerald-200",
  lead_manager:       "bg-sky-50 text-sky-700 border-sky-200",
  cold_caller:        "bg-amber-50 text-amber-700 border-amber-200",
};

const DISPO_TONE = {
  connected:        "bg-emerald-50 text-emerald-700",
  "interested-hot": "bg-red-50 text-red-700",
  "interested-warm":"bg-orange-50 text-orange-700",
  callback:         "bg-sky-50 text-sky-700",
  "not-interested": "bg-slate-100 text-slate-600",
  voicemail:        "bg-purple-50 text-purple-700",
  "no-answer":      "bg-neutral-100 text-neutral-600",
  "wrong-number":   "bg-yellow-50 text-yellow-700",
  dnc:              "bg-red-100 text-red-800",
  "market-value":   "bg-teal-50 text-teal-700",
};

const fmtTime = (secs) => {
  if (!secs) return "0s";
  const s = Math.round(secs);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return `${m}m ${rest}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

export default function TeamAnalytics({ hideHeader = false }) {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/analytics", { params: { days: d } });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analytics load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(days); }, [days]);

  const totals = rows.reduce((acc, r) => ({
    dials:        acc.dials + r.dials,
    connects:     acc.connects + r.connects,
    talk_seconds: acc.talk_seconds + r.talk_seconds,
    abandoned:    acc.abandoned + r.abandoned,
  }), { dials: 0, connects: 0, talk_seconds: 0, abandoned: 0 });
  const teamConnectRate = totals.dials ? Math.round((totals.connects / totals.dials) * 1000) / 10 : 0;

  return (
    <div className="px-6 md:px-10 pb-16" data-testid="team-analytics-page">
      {!hideHeader && (
        <div className="pt-6 md:pt-10">
          <span className="app-eyebrow">/ Team</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Team Analytics</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            Per-agent scoreboard — dials, connects, talk time, and dispositions.
            Spot who&apos;s crushing it and who could use coaching.
          </p>
        </div>
      )}

      {/* Time range selector */}
      <div className="flex items-center gap-2 mt-6" data-testid="analytics-range">
        <span className="text-xs text-muted-foreground">Time range:</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`analytics-days-${d}`}
            className={`text-xs px-3 py-1 rounded-full border transition ${
              days === d
                ? "bg-[var(--app-black)] text-white border-[var(--app-black)]"
                : "bg-white text-muted-foreground border-[var(--app-line)] hover:bg-[var(--app-surface)]"
            }`}
          >
            Last {d} days
          </button>
        ))}
      </div>

      {/* Team-wide summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="analytics-tiles">
        <SummaryTile icon={Phone}       label="Total dials"    value={totals.dials.toLocaleString()} tone="neutral" />
        <SummaryTile icon={PhoneCall}   label="Live connects"  value={totals.connects.toLocaleString()}
                     hint={`${teamConnectRate}% connect rate`} tone="emerald" />
        <SummaryTile icon={Clock}       label="Total talk time" value={fmtTime(totals.talk_seconds)} tone="sky" />
        <SummaryTile icon={ShieldAlert} label="Abandoned (power-dial)" value={totals.abandoned.toLocaleString()}
                     tone={totals.abandoned > 0 ? "red" : "neutral"} />
      </div>

      {/* Per-agent table */}
      <div className="mt-6 card-flat overflow-hidden" data-testid="analytics-table">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-line)]">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Per-agent scoreboard
          </div>
          {loading && <Loader2 size={12} className="animate-spin text-muted-foreground" />}
        </div>
        {rows.length === 0 && !loading ? (
          <div className="text-center py-12 text-sm text-muted-foreground">
            <TrendingUp size={30} className="mx-auto mb-2 opacity-30" />
            No call activity in this window yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-[var(--app-line)]">
                <tr>
                  <th className="px-4 py-2.5 text-left w-10">#</th>
                  <th className="px-4 py-2.5 text-left">Agent</th>
                  <th className="px-4 py-2.5 text-right">Dials</th>
                  <th className="px-4 py-2.5 text-right">Connects</th>
                  <th className="px-4 py-2.5 text-right">Rate</th>
                  <th className="px-4 py-2.5 text-right">Talk time</th>
                  <th className="px-4 py-2.5 text-right">Avg call</th>
                  <th className="px-4 py-2.5 text-right">Abandoned</th>
                  <th className="px-4 py-2.5 text-left pl-4">Top outcomes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const topDispos = Object.entries(r.dispositions || {})
                    .filter(([, v]) => v > 0)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 4);
                  const rateTone =
                    r.connect_rate >= 20 ? "text-emerald-700 font-semibold" :
                    r.connect_rate >= 10 ? "text-sky-700" :
                    r.dials > 0        ? "text-amber-700" : "text-muted-foreground";
                  return (
                    <tr key={r.user_id}
                        className="border-b border-[var(--app-line)] last:border-0 hover:bg-[var(--app-surface)]/60 transition"
                        data-testid={`analytics-row-${r.user_id}`}>
                      <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{r.rank}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.name}</div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                          <span className="truncate max-w-[180px]">{r.email}</span>
                          <span className={`text-[9px] uppercase font-mono px-1.5 py-0.5 rounded border ${ROLE_TONE[r.role] || "bg-neutral-100"}`}>
                            {(r.role || "").replace(/_/g, " ")}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{r.dials}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.connects}</td>
                      <td className={`px-4 py-3 text-right font-mono ${rateTone}`}>{r.connect_rate}%</td>
                      <td className="px-4 py-3 text-right font-mono">{fmtTime(r.talk_seconds)}</td>
                      <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                        {r.connects ? fmtTime(r.avg_talk_seconds) : "—"}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${r.abandoned > 0 ? "text-red-700" : "text-muted-foreground"}`}>
                        {r.abandoned}
                      </td>
                      <td className="px-4 py-3 pl-4">
                        {topDispos.length === 0 ? (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {topDispos.map(([k, v]) => (
                              <span key={k}
                                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${DISPO_TONE[k] || "bg-neutral-100"}`}>
                                {k.replace(/-/g, " ")} · {v}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, hint, tone = "neutral" }) {
  const toneCls = {
    neutral: "border-[var(--app-line)]",
    emerald: "border-emerald-200 bg-emerald-50/40",
    sky:     "border-sky-200 bg-sky-50/40",
    red:     "border-red-200 bg-red-50/40",
  }[tone] || "border-[var(--app-line)]";
  return (
    <div className={`rounded-2xl border ${toneCls} p-4`} data-testid={`analytics-tile-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon size={11} /> {label}
      </div>
      <div className="mt-2 text-2xl font-display">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}
